import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Badge, type BadgeTone } from '../../../../shared/components/Badge.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { EligibilityPicker } from './EligibilityPicker.js';
import { DetailList } from './DetailList.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** A payment of the bill (t182), as GET /v1/payments/transaction/eligibility/:id lists it. */
interface Txn {
  beneficiaryName: string;
  netAmount: number;
  paymentModeCode: string;
  transactionStatusCode: string;
  utrNumber: string | null;
  bankReferenceNo: string | null;
  failureReason: string | null;
  paymentDate: string | null;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  SUCCESS: 'success',
  FAILED: 'danger',
  RETURNED: 'warning',
  REVERSED: 'warning',
};

/** L15.12 — Payment Tracking: where a bill's payment stands. */
export function PaymentTrackingPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [eligId, setEligId] = useState(params.get('eligibilityId') ?? '');
  const [stage, setStage] = useState('');
  const [data, setData] = useState<Txn | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    // Every payment of the bill is kept; the newest comes first.
    apiRequest<Txn[]>(`/v1/payments/transaction/eligibility/${id.trim()}`, { token })
      .then((rows) => setData(rows[0] ?? null))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = params.get('eligibilityId');
    if (id) load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = data?.transactionStatusCode;

  return (
    <div className="tl-page" style={{ padding: 24, maxWidth: 960 }}>
      <PageHeader title={t('finance.tracking.title')} subtitle={t('finance.tracking.subtitle')} />
      <EligibilityPicker
        value={eligId}
        onPick={(id, s) => {
          setEligId(id);
          setStage(s);
          setParams(id ? { eligibilityId: id } : {});
          if (id) load(id); else { setData(null); setError(null); }
        }}
      />
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.tracking.loading')}</p>}
      {!loading && !data && !error && !eligId && <EmptyState title={t('finance.tracking.empty')} />}
      {!loading && !data && !error && eligId && stage && (
        <Alert tone="info">{t('finance.tracking.notPaid', { stage: t(`finance.stage.${stage}`) })}</Alert>
      )}
      {data && (
        <Card
          data-testid="tracking-card"
          title={(
            <div className="tl-stack tl-stack--row" style={{ gap: 12, alignItems: 'center' }}>
              <h2 className="tl-card__title" style={{ margin: 0 }}>{data.beneficiaryName ?? '—'}</h2>
              {status && <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>{t(`finance.txnStatus.${status}`)}</Badge>}
            </div>
          )}
        >
          <DetailList
            items={[
              { label: t('finance.tracking.rowAmount'), value: money(data.netAmount), strong: true },
              { label: t('finance.tracking.rowMode'), value: data.paymentModeCode ?? '—' },
              { label: t('finance.tracking.rowUtr'), value: data.utrNumber ?? '—', testId: 'tracking-utr' },
              { label: t('finance.tracking.rowBankRef'), value: data.bankReferenceNo ?? '—' },
              { label: t('finance.tracking.rowValueDate'), value: day(data.paymentDate) },
              ...(data.failureReason ? [{ label: t('finance.tracking.rowFailure'), value: data.failureReason }] : []),
            ]}
          />
        </Card>
      )}
    </div>
  );
}
